import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faEnvelope } from "@fortawesome/pro-regular-svg-icons";

import { TicketPriority, TicketStatus } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

/** Staff colours (PREC-3939 decision 4): what waits on us is red, not the customer's calm blue. */
const STATUS_CLASSES: Record<TicketStatus, string> = {
	[TicketStatus.OPEN]: "bg-blue-action-200 text-brand-light",
	[TicketStatus.WAITING_ON_US]: "bg-status-error-light text-status-error",
	[TicketStatus.WAITING_ON_CUSTOMER]: "bg-status-warning-light text-status-wrong-person",
	[TicketStatus.RESOLVED]: "bg-status-success-light text-status-success",
	[TicketStatus.CLOSED]: "bg-status-info-light text-status-info",
};

const PRIORITY_TEXT: Record<TicketPriority, string> = {
	[TicketPriority.URGENT]: "text-status-error",
	[TicketPriority.HIGH]: "text-status-wrong-person",
	[TicketPriority.NORMAL]: "text-text",
	[TicketPriority.LOW]: "text-text-meta",
};

const PRIORITY_DOT: Record<TicketPriority, string> = {
	[TicketPriority.URGENT]: "bg-status-error",
	[TicketPriority.HIGH]: "bg-status-warning",
	[TicketPriority.NORMAL]: "bg-status-info",
	[TicketPriority.LOW]: "bg-status-disabled",
};

const PILL =
	"inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-xs font-medium";

export const StaffTicketStatusPill: FunctionComponent<{ status: TicketStatus }> = ({ status }) => {
	const { t } = useTranslation();

	return (
		<span className={classNames(PILL, STATUS_CLASSES[status])}>
			<span className="h-1.5 w-1.5 rounded-full bg-current" />
			{t(`support.staff.status.${status}`)}
		</span>
	);
};

export const TicketPriorityBadge: FunctionComponent<{ priority: TicketPriority }> = ({
	priority,
}) => {
	const { t } = useTranslation();

	return (
		<span
			className={classNames(
				"inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold",
				PRIORITY_TEXT[priority]
			)}
		>
			<span className={classNames("h-2 w-2 rounded-full", PRIORITY_DOT[priority])} />
			{t(`support.staff.priority.${priority}`)}
		</span>
	);
};

/**
 * The ticket arrived as a mail to support@ (PREC-3978), so a reply is mailed straight to the
 * external sender rather than posted to an in-app Support page — this pill is the header's only
 * cue that a reply here goes out over e-mail.
 */
export const TicketSourceEmailBadge: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<span className={classNames(PILL, "bg-status-info-light text-status-info")}>
			<Icon icon={faEnvelope} />
			{t("support.staff.detail.source.email")}
		</span>
	);
};

/**
 * "UI off" (spec §8): the ticket's company lacks SUPPORT_TICKETS, so the customer gets e-mails but
 * has no Support page to reply on.
 */
export const SupportUiOffMarker: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<Tooltip content={t("support.staff.uiOffHint")}>
			<span
				className="inline-flex h-5 flex-shrink-0 items-center whitespace-nowrap rounded border border-gray-200 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-meta"
				data-testid="ui-off"
			>
				{t("support.staff.uiOff")}
			</span>
		</Tooltip>
	);
};
